import { FormSelectDataType, ListBoxSelect, toast } from '@systemeio/ui';
import NProgress from 'nprogress';
import { useEffect, useMemo, useState } from 'react';
import { setWorkspace } from 'shared/api/workspace-change-api';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useRouter } from 'shared/hooks/use-router';
import useUser from 'shared/hooks/use-user';
import { useSWRConfig } from 'swr';
import { twJoin, twMerge } from 'tailwind-merge';
import { ChevronDownIcon } from '@systemeio/ui/icons';
import { hasWorkspaces } from 'shared/hooks/use-user/utils/has-workspaces';
import { UserWorkspaceRolesEnum } from 'shared/enums/user-workspace-roles-enum';
import { pageRoute } from 'shared/constants/routes.constants';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
import { META_DATA_FROM_SERVER_KEY } from 'shared/hooks/use-metadata-from-server';
interface WorkspaceSelectorProps {
  afterChange?: () => void;
  className?: string;
}
interface WorkspaceDataInterface {
  id: number | null;
  image: string;
  name: string;
  role: UserWorkspaceRolesEnum | null;
}
function WorkspaceSelector({
  afterChange,
  className
}: WorkspaceSelectorProps) {
  const {
    user,
    avatarUrl
  } = useUser();
  const router = useRouter();
  const [id, setId] = useState<number | null>(null);
  const {
    mutate
  } = useSWRConfig();
  const {
    t
  } = useLocoTranslation();
  const [isFetching, setIsFetching] = useState(false);
  useEffect(() => {
    if (!hasWorkspaces(user)) return;
    const selectedWorkspace = user.workspaceMemberships.find(el => el.workspaceOwner.id === user?.workspaceOwner);
    setId(selectedWorkspace?.id ?? null);
  }, [user]);
  const workspaces = useMemo(() => {
    if (!user) return;
    const arr: WorkspaceDataInterface[] = [];
    if (user.subdomain) {
      arr.push({
        id: null,
        name: user.subdomain,
        image: avatarUrl ?? '',
        role: null
      });
    }
    if (hasWorkspaces(user)) {
      user.workspaceMemberships.forEach(workspace => {
        arr.push({
          id: workspace.id,
          name: workspace.name,
          image: workspace.image,
          role: workspace.role
        });
      });
    }
    return arr;
  }, [avatarUrl, user]);
  const redirectToPath = async (route: string) => {
    if (router.pathname === route) return;
    await router.replace(route);
  };
  const clearSWRData = async () => {
    await mutate(key => key !== META_DATA_FROM_SERVER_KEY, undefined, {
      revalidate: false
    });
  };
  const revalidateSWRData = async () => {
    await mutate(key_0 => key_0 !== META_DATA_FROM_SERVER_KEY);
  };
  const getWorkspaceRedirectRoute = (role: UserWorkspaceRolesEnum | null) => {
    if (role === UserWorkspaceRolesEnum.workspaceAssistant) {
      return getPageRoute(pageRoute.Funnels);
    }
    return getPageRoute(pageRoute.Dashboard);
  };
  const onWorkspaceChange = async (workspaceId: number | null) => {
    if (workspaceId === id) return;
    const workspace_0 = workspaces?.find(w => w.id === workspaceId);
    if (!workspace_0) return;
    try {
      setIsFetching(true);
      NProgress.start();
      await setWorkspace({
        currentWorkspaceMembership: workspaceId
      });
      afterChange?.();
      await clearSWRData();
      await redirectToPath(getWorkspaceRedirectRoute(workspace_0.role));
      await revalidateSWRData();
      toast.success(t('dashboard.notifications.workspace_saved'));
    } catch {} finally {
      setIsFetching(false);
      NProgress.done();
    }
  };
  const workspacesData: FormSelectDataType<number | null>[] | undefined = workspaces?.map(el_0 => ({
    id: el_0.id,
    caption: el_0.name,
    customCaption: <div className={`text-start flex items-center gap-3`}>
        <img className="h-7 w-7 overflow-hidden rounded-full object-cover" src={el_0.image} alt={el_0.name} />
        {el_0.name}
      </div>
  }));
  const selectedWorkspace_0 = workspaces?.find(el_1 => el_1.id === id);
  return hasWorkspaces(user) && selectedWorkspace_0 ? <ListBoxSelect buttonClassName={'rounded-md'} disabled={isFetching} value={id} onChange={onWorkspaceChange} data={workspacesData}>
      {open => <div className={twMerge(`border-text-secondary/30 text-text-secondary grid grid-flow-col items-center gap-3 rounded-md border bg-transparent px-4 py-2`, isFetching && 'bg-text-tertiary/50 animate-pulse', className)}>
          <img className="h-[28px] w-[28px] min-w-[28px] overflow-hidden rounded-full object-cover" src={selectedWorkspace_0.image} alt={selectedWorkspace_0.name} />
          <span className="text-start truncate">{selectedWorkspace_0.name}</span>
          <ChevronDownIcon className={twJoin(`h-3 w-3 transition-transform`, open && 'rotate-180')} aria-hidden="true" />
        </div>}
    </ListBoxSelect> : null;
}
export default WorkspaceSelector;